// @ts-check
// Mapa de calor dos leads por CEP (contrato 07, seção 9): mapa coroplético em SVG próprio (UFs do Brasil, municípios
// da UF escolhida, CEPs com coordenada dentro da cidade), escala sequencial do tema, legenda, dica com os números,
// ranking ao lado (embaixo no celular), trilha para voltar e a qualidade dos dados de endereço. A medida e o nível
// são do cartão: trocar não pede dados de novo, porque a resposta já traz os três níveis.
// Clique no gráfico (contrato 07, seção 10): clicar na área continua descendo de nível; a lista abre pelos links
// "ver N leads" do ranking e da dica fixada pelo toque, por "Ver os N leads desta área" na trilha e, no último nível
// (bairros e CEPs), pelo próprio clique. O conjunto segue a medida ativa e a qualidade dos dados abre sem limite.
import { html } from 'htm/preact';
import { Link } from '../../app/router.js';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { fetchGeoMesh } from '../../data/dashboards.js';
import { t } from '../../i18n/index.js';
import {
  EMPTY_GEO_PATH, GEO_NEIGHBORHOOD_RANKING_LIMIT, GEO_RANKING_LIMIT, drillInto, geoAreasFor, geoBreadcrumb, geoLevelOf, geoRanking, geoScaleFor,
  geoQualityFixes, meshCodeOf,
} from '../../lib/geo-map.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { canReadObjectRecords } from '../../lib/permissions.js';
import { geoSetCount } from '../../lib/drilldown-links.js';
import { DrillLink, handleDrillLinkClick, useChartPointNavigation, useDrilldown } from './drilldown.js';
import { GEO_MAP_METRICS, colorClassOf, colorStepOf, geoMetricValue, isRateMetric, legendRanges } from '../../../../shared/geo/geo-metrics.js';
import { aspectHeight, createProjection, ringsBoundingBox, ringsToPathData } from '../../../../shared/geo/geo-mesh.js';
import { formatMetricValue } from './widget-format.js';

/**
 * @typedef {import('../../lib/geo-map.js').GeoMapData} GeoMapData
 * @typedef {import('../../lib/geo-map.js').GeoPath} GeoPath
 * @typedef {import('../../lib/geo-map.js').GeoArea} GeoArea
 * @typedef {import('../../../../shared/geo/geo-mesh.js').GeoMesh} GeoMesh
 * @typedef {{ title: string, counts: { leads: number, withVisit: number, enrollments: number }, x: number, y: number, canDrill: boolean, flip: boolean,
 *   pinned?: { href: string | null, code: string | null } }} Hover
 */

const MAP_WIDTH = 640;
// nível da área no ponto do clique, pelo nível do mapa aberto
const AREA_LEVEL_BY_MAP_LEVEL = /** @type {Record<string, string>} */ ({ STATES: 'STATE', CITIES: 'CITY', NEIGHBORHOODS: 'NEIGHBORHOOD' });

/**
 * Ponto do clique de uma área na medida ativa.
 * @param {string} level ALL, STATE, CITY, NEIGHBORHOOD ou POINT @param {string | null} key @param {string} metric
 * @returns {import('./drilldown.js').DrilldownPoint}
 */
const geoAreaPoint = (level, key, metric) => ({ part: 'geoArea', level, ...(key ? { key } : {}), metric });

/** Texto do link "ver N leads" na medida ativa. @param {string} metric @param {number} count */
const seeLeadsText = (metric, count) => t(`dashboardDrilldown.geo.seeLeads.${geoSetMetric(metric)}`, { count });

const VISIT_METRICS = Object.freeze(['WITH_VISIT', 'VISIT_RATE']);

/** Medida do conjunto: taxa abre o numerador. @param {string} metric */
const geoSetMetric = (metric) => (metric === 'VISIT_RATE' ? 'WITH_VISIT' : metric === 'CONVERSION_RATE' ? 'ENROLLMENTS' : metric === 'WITH_VISIT' || metric === 'ENROLLMENTS' ? metric : 'LEADS');

/** @param {string} metric */
const formatOf = (metric) => (isRateMetric(metric) ? 'PERCENT' : 'NUMBER');

/** Cor da classe na escala sequencial do tema (verde água, do claro ao forte). @param {number} classIndex @param {number} classTotal */
function fillOf(classIndex, classTotal) {
  const step = colorStepOf(classIndex, classTotal);
  return step === null ? 'var(--geo-map-empty)' : `var(--t-color-turquoise${step})`;
}

/** Malha do IBGE do nível, com estado de carga e nova tentativa. @param {string} code */
function useGeoMesh(code) {
  const [state, setState] = useState(/** @type {{ code: string, mesh: GeoMesh | null, status: 'loading' | 'ready' | 'error' }} */ ({ code, mesh: null, status: 'loading' }));
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let isCancelled = false;
    setState({ code, mesh: null, status: 'loading' });
    fetchGeoMesh(code)
      .then((mesh) => {
        if (!isCancelled) setState({ code, mesh, status: 'ready' });
      })
      .catch(() => {
        if (!isCancelled) setState({ code, mesh: null, status: 'error' });
      });
    return () => {
      isCancelled = true;
    };
  }, [code, attempt]);
  return { ...state, retry: () => setAttempt((count) => count + 1) };
}

/** @param {{ counts: { leads: number, withVisit: number | null, enrollments: number } }} props */
function TooltipNumbers({ counts }) {
  const visitRate = geoMetricValue(counts, 'VISIT_RATE');
  const conversionRate = geoMetricValue(counts, 'CONVERSION_RATE');
  // withVisit null: o papel não lê a data da visita nem o comparecimento
  return html`
    <strong class="geo-map-tooltip-count">${t('geoMap.tooltip.leads', { count: counts.leads })}</strong>
    ${counts.withVisit === null ? null : html`<span>${t('geoMap.tooltip.withVisit', { value: formatMetricValue(counts.withVisit, 'NUMBER'), rate: formatMetricValue(visitRate, 'PERCENT') })}</span>`}
    <span>${t('geoMap.tooltip.enrollments', { value: formatMetricValue(counts.enrollments, 'NUMBER'), rate: formatMetricValue(conversionRate, 'PERCENT') })}</span>`;
}

/**
 * Mapa em SVG do nível: áreas da malha (UFs ou municípios) ou o contorno da cidade com os CEPs.
 * @param {{ mesh: GeoMesh, path: GeoPath, data: GeoMapData, areas: GeoArea[], metric: string, breaks: number[], values: Map<string, number | null>,
 *   onDrill: (code: string) => void, onHover: (hover: Hover | null) => void, pinnedCode: string | null }} props
 */
function GeoMapSvg({ mesh, path, data, areas, metric, breaks, values, onDrill, onHover, pinnedCode }) {
  const level = geoLevelOf(path);
  const areasByCode = useMemo(() => new Map(areas.filter((area) => area.code).map((area) => [/** @type {string} */ (area.code), area])), [areas]);
  const city = level === 'NEIGHBORHOODS' ? mesh.features.find((feature) => feature.code === path.cityCode) ?? null : null;
  const bbox = city ? ringsBoundingBox(city.rings) : mesh.bbox;
  const height = Math.max(240, Math.min(720, aspectHeight(bbox, MAP_WIDTH)));
  const { project } = createProjection(bbox, MAP_WIDTH, height, 12);
  const svgRef = useRef(/** @type {SVGSVGElement | null} */ (null));
  const pointerTypeRef = useRef('mouse');
  const drill = useDrilldown();

  /** @param {Element} element @param {Hover['title']} title @param {Hover['counts']} counts @param {boolean} canDrill @param {MouseEvent | null} event @param {Hover['pinned']} [pinned] */
  const showHover = (element, title, counts, canDrill, event, pinned) => {
    const box = svgRef.current?.parentElement?.getBoundingClientRect();
    if (!box) return;
    const target = element.getBoundingClientRect();
    const x = event ? event.clientX - box.left : target.left + target.width / 2 - box.left;
    const y = event ? event.clientY - box.top : target.top + target.height / 2 - box.top;
    // perto da borda direita a dica abre para a esquerda, para não cobrir o ranking nem sair do cartão
    onHover({ title, counts, x, y, canDrill, flip: x > box.width - 260, ...(pinned ? { pinned } : {}) });
  };

  // CEPs da cidade: o último nível, o clique abre a lista; o link de cada ponto só é montado no ponteiro ou no foco
  const cityPoints = level === 'NEIGHBORHOODS' ? data.points.filter((point) => point.cityCode === path.cityCode) : [];
  const navigationPoints = drill
    ? cityPoints.flatMap((point, index) => {
      const pointDescriptor = geoAreaPoint('POINT', point.postcode, metric);
      const count = geoSetCount(point, metric);
      if (count <= 0 || !drill.canOpen(pointDescriptor)) return [];
      return [{ id: point.postcode, categoryIndex: index, seriesIndex: 0, href: null, resolveHref: () => drill.target(pointDescriptor, count)?.href ?? null }];
    })
    : [];
  const navigationById = new Map(navigationPoints.map((point) => [point.id, point]));
  const navigation = useChartPointNavigation({
    points: navigationPoints,
    label: t('geoMap.mapLabel.NEIGHBORHOODS', { name: geoBreadcrumb(data, path)[2]?.label ?? '' }),
    onPin: () => {},
  });

  const regionLabel = level === 'STATES' ? t('geoMap.mapLabel.STATES')
    : t(`geoMap.mapLabel.${level}`, { name: level === 'CITIES' ? geoBreadcrumb(data, path)[1]?.label ?? '' : geoBreadcrumb(data, path)[2]?.label ?? '' });

  if (level === 'NEIGHBORHOODS') {
    const points = cityPoints;
    const pointBreaks = geoScaleFor(points.map((point) => ({ ...point, id: point.postcode, code: null, label: point.postcode })), metric).breaks;
    const maximumLeads = Math.max(1, ...points.map((point) => point.leads));
    const neighborhoodLabel = new Map(data.neighborhoods.map((neighborhood) => [neighborhood.key, neighborhood.label ?? t('geoMap.unnamedNeighborhood')]));
    return html`<svg ref=${(/** @type {SVGSVGElement | null} */ element) => {
      svgRef.current = element;
      navigation.containerProps.ref.current = element;
    }} class="geo-map-svg" viewBox=${`0 0 ${MAP_WIDTH} ${height}`} preserveAspectRatio="xMidYMid meet"
      role=${navigationPoints.length > 0 ? 'group' : 'img'} aria-label=${navigationPoints.length > 0 ? navigation.containerProps['aria-label'] : regionLabel}
      onKeyDown=${navigation.containerProps.onKeyDown} onPointerDown=${navigation.containerProps.onPointerDown}>
      ${city ? html`<path class="geo-map-outline" d=${ringsToPathData(city.rings, project)} />` : null}
      ${points.map((point) => {
        const [x, y] = project(point.longitude, point.latitude);
        const value = geoMetricValue(point, metric);
        const label = `${t('geoMap.pointLabel', { postcode: point.postcode })} · ${neighborhoodLabel.get(point.neighborhoodKey ?? '') ?? ''}`;
        const valueText = formatMetricValue(value, formatOf(metric));
        const navigationPoint = navigationById.get(point.postcode);
        const circle = html`<circle class="geo-map-point" cx=${x} cy=${y} r=${4 + 10 * Math.sqrt(point.leads / maximumLeads)}
          data-drillable=${navigationPoint ? 'true' : undefined}
          fill=${fillOf(colorClassOf(value, pointBreaks), pointBreaks.length)} tabindex=${navigationPoint ? undefined : '0'}
          aria-label=${navigationPoint ? undefined : `${label}: ${valueText}`}
          onMouseMove=${(/** @type {MouseEvent} */ event) => showHover(/** @type {Element} */ (event.currentTarget), label, point, false, event)}
          onMouseLeave=${() => onHover(null)}
          onFocus=${navigationPoint ? undefined : (/** @type {FocusEvent} */ event) => showHover(/** @type {Element} */ (event.currentTarget), label, point, false, null)}
          onBlur=${navigationPoint ? undefined : () => onHover(null)} />`;
        if (!navigationPoint) return html`<g key=${point.postcode}>${circle}</g>`;
        const linkProps = navigation.pointProps(navigationPoint, { label, valueText });
        return html`<a key=${point.postcode} ...${linkProps}
          onFocus=${(/** @type {FocusEvent} */ event) => {
            linkProps.onFocus(event);
            showHover(/** @type {Element} */ (event.currentTarget), label, point, false, null);
          }}
          onBlur=${() => onHover(null)}>${circle}</a>`;
      })}
    </svg>`;
  }

  return html`<svg ref=${svgRef} class="geo-map-svg" viewBox=${`0 0 ${MAP_WIDTH} ${height}`} preserveAspectRatio="xMidYMid meet" role="group" aria-label=${regionLabel}>
    ${mesh.features.map((feature) => {
      const area = areasByCode.get(feature.code);
      const value = area ? values.get(area.id) ?? null : null;
      const canDrill = Boolean(area && area.leads > 0);
      const name = area?.label || feature.name || feature.code;
      const counts = area ?? { leads: 0, withVisit: 0, enrollments: 0 };
      const areaLevel = AREA_LEVEL_BY_MAP_LEVEL[level];
      const setCount = area ? geoSetCount(area, metric) : 0;
      /** @param {MouseEvent} event */
      const handleAreaClick = (event) => {
        // no toque, o primeiro toque fixa a dica com "ver N leads" e "ver por dentro"; o segundo desce de nível
        if (pointerTypeRef.current === 'touch' && pinnedCode !== feature.code) {
          const href = drill && setCount > 0 ? drill.target(geoAreaPoint(areaLevel, feature.code, metric), setCount)?.href ?? null : null;
          showHover(/** @type {Element} */ (event.currentTarget), name, counts, canDrill, event, { href, code: feature.code });
          return;
        }
        onDrill(feature.code);
      };
      return html`<path key=${feature.code} class="geo-map-area" data-has-data=${canDrill ? 'true' : undefined}
        d=${ringsToPathData(feature.rings, project)} fill=${fillOf(colorClassOf(value, breaks), breaks.length)}
        tabindex=${canDrill ? '0' : undefined} role=${canDrill ? 'button' : undefined}
        aria-label=${`${name}: ${canDrill ? formatMetricValue(value, formatOf(metric)) : t('geoMap.legendEmpty')}`}
        onPointerDown=${(/** @type {PointerEvent} */ event) => {
          pointerTypeRef.current = event.pointerType || 'mouse';
        }}
        onClick=${canDrill ? handleAreaClick : undefined}
        onKeyDown=${canDrill ? (/** @type {KeyboardEvent} */ event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onDrill(feature.code);
        } : undefined}
        onMouseMove=${(/** @type {MouseEvent} */ event) => {
          if (!pinnedCode) showHover(/** @type {Element} */ (event.currentTarget), name, counts, canDrill, event);
        }}
        onMouseLeave=${() => {
          if (!pinnedCode) onHover(null);
        }}
        onFocus=${(/** @type {FocusEvent} */ event) => showHover(/** @type {Element} */ (event.currentTarget), name, counts, canDrill, null)}
        onBlur=${() => {
          if (!pinnedCode) onHover(null);
        }} />`;
    })}
  </svg>`;
}

/**
 * @param {{ breaks: number[], metric: string }} props
 */
function GeoLegend({ breaks, metric }) {
  const format = formatOf(metric);
  return html`<div class="geo-map-legend" aria-label=${t(`geoMap.metrics.${metric}`)}>
    <span class="geo-map-legend-title">${t(`geoMap.metrics.${metric}`)}</span>
    <ul class="geo-map-legend-items">
      <li><span class="geo-map-swatch" style=${{ background: 'var(--geo-map-empty)' }}></span>${t('geoMap.legendEmpty')}</li>
      ${legendRanges(breaks, metric).map((range, index) => html`<li key=${index}>
        <span class="geo-map-swatch" style=${{ background: fillOf(index, breaks.length) }}></span>
        ${range.from === range.to ? formatMetricValue(range.to, format) : t('geoMap.legendRange', { from: formatMetricValue(range.from, format), to: formatMetricValue(range.to, format) })}
      </li>`)}
    </ul>
  </div>`;
}

/**
 * Ranking do nível com barras; clicar desce de nível como no mapa e "ver N leads" abre a lista da área. No nível de
 * bairro, o último, o próprio item abre a lista.
 * @param {{ areas: GeoArea[], metric: string, level: string, onDrill: ((code: string) => void) | null }} props
 */
function GeoRanking({ areas, metric, level, onDrill }) {
  const drill = useDrilldown();
  const limit = level === 'NEIGHBORHOODS' ? GEO_NEIGHBORHOOD_RANKING_LIMIT : GEO_RANKING_LIMIT;
  const { items, others } = geoRanking(areas, metric, limit);
  const format = formatOf(metric);
  const maximum = isRateMetric(metric) ? 100 : Math.max(1, ...items.map((area) => geoMetricValue(area, metric) ?? 0));
  const areaLevel = AREA_LEVEL_BY_MAP_LEVEL[level];
  return html`<section class="geo-map-ranking" aria-label=${t(`geoMap.rankingTitle.${level}`, { metric: t(`geoMap.metrics.${metric}`) })}>
    <h4 class="geo-map-ranking-title">${t(`geoMap.rankingTitle.${level}`, { metric: t(`geoMap.metrics.${metric}`) })}</h4>
    ${items.length === 0 ? html`<p class="report-footnote">${t('geoMap.noLeadsHere')}</p>` : null}
    <ol class="report-bars geo-map-ranking-list">
      ${items.map((area) => {
        const value = geoMetricValue(area, metric);
        const label = area.isUnnamed ? t('geoMap.unnamedNeighborhood') : area.label;
        const valueText = isRateMetric(metric)
          ? t('geoMap.rateDetail', { rate: formatMetricValue(value, 'PERCENT'), leads: t('geoMap.tooltip.leads', { count: area.leads }) })
          : formatMetricValue(value, format);
        const canDrill = Boolean(onDrill && area.code);
        const setCount = geoSetCount(area, metric);
        const point = geoAreaPoint(areaLevel, area.code ?? area.id, metric);
        const head = html`<div class="report-bar-head">
            <span class="report-bar-label"><span class="report-bar-label-text" title=${label}>${label}</span></span>
            <span class="report-bar-value">${valueText}</span>
          </div>
          <div class="report-bar-track" aria-hidden="true"><span class="report-bar-fill" style=${{ width: `${Math.max(0, Math.min(100, ((value ?? 0) / maximum) * 100))}%` }}></span></div>`;
        const target = drill && setCount > 0 ? drill.target(point, setCount) : null;
        return html`<li class="report-bar geo-map-ranking-item" key=${area.id} data-tone="VOLUME">
          ${canDrill
            ? html`<button type="button" class="geo-map-ranking-button" onClick=${() => onDrill?.(/** @type {string} */ (area.code))}>${head}</button>`
            : head}
          ${target
            ? html`<${DrillLink} point=${point} value=${setCount} label=${label} valueText=${seeLeadsText(metric, setCount)} className="geo-map-see-leads"
                stretched=${!canDrill}>${seeLeadsText(metric, setCount)}<//>`
            : null}
        </li>`;
      })}
    </ol>
    ${others > 0 ? html`<p class="report-footnote">${t('geoMap.rankingOthers', { count: others })}</p>` : null}
  </section>`;
}

/**
 * Leads fora do mapa por motivo: cada motivo abre a lista desses leads, sem limite de quantidade, e o link ao lado
 * leva às famílias desses leads, onde o CEP se corrige.
 * @param {{ quality: GeoMapData['quality'], households?: GeoMapData['qualityHouseholds'] }} props
 */
function GeoQuality({ quality, households }) {
  const drill = useDrilldown();
  // o CEP mora na família: o link leva às famílias, com o CEP editável na lista (contrato 07, seção 9.9)
  const householdObject = objectsByNameSingular.value.get('household');
  const canOpenHouseholds = Boolean(householdObject && canReadObjectRecords(householdObject));
  // "(corrigir nas N famílias; 1 lead sem família: vincule uma família no card)": tudo visível, também no celular
  /** @param {'withoutPostcode' | 'invalid' | 'pending'} key */
  const householdsLink = (key) => {
    const fixes = key === 'pending' ? null : geoQualityFixes(households?.[key], canOpenHouseholds);
    if (!fixes) return null;
    const link = fixes.householdsHref
      ? html`<${Link} href=${fixes.householdsHref} className="geo-map-quality-link">${fixes.householdsShown < fixes.householdsCount
        ? t('geoMap.quality.firstHouseholdsLink', { count: fixes.householdsShown })
        : t('geoMap.quality.householdsLink', { count: fixes.householdsCount })}<//>`
      : null;
    const orphans = fixes.leadsWithoutHousehold > 0
      ? html`<span class="geo-map-quality-note">${t('geoMap.quality.leadsWithoutHousehold', { count: fixes.leadsWithoutHousehold })}</span>`
      : null;
    return html`${' '}(${link}${link && orphans ? '; ' : ''}${orphans})`;
  };
  /** @param {'withoutPostcode' | 'invalid' | 'pending'} key */
  const problem = (key) => {
    const count = quality[key];
    if (!count) return null;
    const text = t(`geoMap.quality.${key}`, { count });
    const point = { part: 'geoQuality', key };
    const title = key === 'pending' ? t('geoMap.quality.pendingHint') : undefined;
    const tone = key === 'pending' ? undefined : 'ALERT';
    const families = householdsLink(key);
    if (!drill?.target(point, count)) return html`<span data-tone=${tone} title=${title}>${text}${families}</span>`;
    const leads = html`<${DrillLink} point=${point} value=${count} label=${text} valueText=${formatMetricValue(count, 'NUMBER')}
      className="geo-map-quality-link" data-tone=${tone} hint=${title ? `${title} ${t('dashboardDrilldown.tooltipHint')}` : undefined}>${text}<//>`;
    return families ? html`<span data-tone=${tone}>${leads}${families}</span>` : leads;
  };
  return html`<footer class="geo-map-quality" aria-label=${t('geoMap.quality.label')}>
    <span>${t('geoMap.quality.located', { located: formatMetricValue(quality.located, 'NUMBER'), total: formatMetricValue(quality.total, 'NUMBER') })}</span>
    ${problem('withoutPostcode')}
    ${problem('invalid')}
    ${problem('pending')}
  </footer>`;
}

/** @param {{ data: GeoMapData }} props */
export function GeoMapWidget({ data }) {
  // papel sem a regra da visita recebe withVisit null: as medidas de visita saem das abas
  const metrics = data.totals?.withVisit === null ? GEO_MAP_METRICS.filter((key) => !VISIT_METRICS.includes(key)) : GEO_MAP_METRICS;
  const [metric, setMetric] = useState(metrics.includes(data.metric) ? data.metric : 'LEADS');
  const [path, setPath] = useState(/** @type {GeoPath} */ (EMPTY_GEO_PATH));
  const [hover, setHover] = useState(/** @type {Hover | null} */ (null));
  // filtros novos podem tirar a UF ou a cidade escolhida: volta para onde ainda há dados
  const safePath = path.cityCode && !data.cities.some((city) => city.code === path.cityCode)
    ? { stateCode: path.stateCode, cityCode: null }
    : path;
  const level = geoLevelOf(safePath);
  const areas = useMemo(() => geoAreasFor(data, safePath), [data, safePath.stateCode, safePath.cityCode]);
  const { values, breaks } = useMemo(() => geoScaleFor(areas, metric), [areas, metric]);
  const meshCode = meshCodeOf(safePath);
  const mesh = useGeoMesh(meshCode);
  const breadcrumb = geoBreadcrumb(data, safePath);
  const drillController = useDrilldown();
  /** @param {string} code */
  const drill = (code) => {
    const next = drillInto(safePath, code);
    if (next) {
      setHover(null);
      setPath(next);
    }
  };
  // "Ver os N leads desta área": o Brasil, a UF ou a cidade aberta, na medida ativa
  const currentArea = safePath.cityCode
    ? { level: 'CITY', key: safePath.cityCode, counts: data.cities.find((city) => city.code === safePath.cityCode) }
    : safePath.stateCode
      ? { level: 'STATE', key: safePath.stateCode, counts: data.states.find((state) => state.code === safePath.stateCode) }
      : { level: 'ALL', key: null, counts: data.totals };
  const currentAreaCount = currentArea.counts ? geoSetCount(currentArea.counts, metric) : 0;
  const currentAreaPoint = geoAreaPoint(currentArea.level, currentArea.key, metric);
  const pinnedHref = hover?.pinned?.href ?? null;
  const canvasRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  // tocar fora do mapa solta a dica fixada pelo toque
  useEffect(() => {
    if (!hover?.pinned) return undefined;
    /** @param {PointerEvent} event */
    const handlePointerDown = (event) => {
      if (!canvasRef.current?.contains(/** @type {Node} */ (event.target))) setHover(null);
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [hover?.pinned]);

  return html`<div class="report-widget geo-map" data-level=${level}>
    <div class="geo-map-toolbar">
      <div class="geo-map-place">
        <nav class="geo-map-breadcrumb" aria-label=${t('geoMap.breadcrumbLabel')}>
          ${breadcrumb.map((item, index) => {
            const label = item.label ?? t('geoMap.brazil');
            const isCurrent = index === breadcrumb.length - 1;
            return html`<span class="geo-map-crumb" key=${item.key}>
              ${index > 0 ? html`<span class="geo-map-crumb-separator" aria-hidden="true">›</span>` : null}
              ${isCurrent
                ? html`<span aria-current="location">${label}</span>`
                : html`<button type="button" class="geo-map-crumb-button" onClick=${() => { setHover(null); setPath(item.path); }}>${label}</button>`}
            </span>`;
          })}
        </nav>
        ${drillController && currentAreaCount > 0
          ? html`<${DrillLink} point=${currentAreaPoint} value=${currentAreaCount} label=${breadcrumb[breadcrumb.length - 1]?.label ?? t('geoMap.brazil')}
              valueText=${formatMetricValue(currentAreaCount, 'NUMBER')} className="geo-map-area-link">
              ${t(`dashboardDrilldown.geo.seeArea.${geoSetMetric(metric)}`, { count: currentAreaCount })}
            <//>`
          : null}
      </div>
      <div class="report-tabs report-tabs-compact geo-map-metrics" role="tablist" aria-label=${t('geoMap.metricLabel')}>
        ${metrics.map((key) => html`<button type="button" role="tab" key=${key} class="report-tab" aria-selected=${metric === key ? 'true' : 'false'}
          onClick=${() => setMetric(key)}>${t(`geoMap.metrics.${key}`)}</button>`)}
      </div>
    </div>
    <div class="geo-map-body">
      <div class="geo-map-canvas" ref=${canvasRef} onMouseLeave=${() => {
        if (!hover?.pinned) setHover(null);
      }}>
        ${mesh.status === 'ready' && mesh.mesh
          ? html`<${GeoMapSvg} mesh=${mesh.mesh} path=${safePath} data=${data} areas=${areas} metric=${metric} breaks=${breaks} values=${values}
              onDrill=${drill} onHover=${setHover} pinnedCode=${hover?.pinned?.code ?? null} />`
          : mesh.status === 'error'
            ? html`<p class="geo-map-message">${t('geoMap.meshError')}
                <button type="button" class="widget-message-link" onClick=${mesh.retry}>${t('geoMap.meshRetry')}</button></p>`
            : html`<p class="geo-map-message">${t('geoMap.meshLoading')}</p>`}
        ${hover
          ? html`<div class="geo-map-tooltip" role="status" data-flip=${hover.flip ? 'true' : undefined} data-pinned=${hover.pinned ? 'true' : undefined}
              style=${{ left: `${hover.x}px`, top: `${hover.y}px` }}>
              <span class="geo-map-tooltip-title">${hover.title}</span>
              <${TooltipNumbers} counts=${hover.counts} />
              ${hover.pinned
                ? html`<span class="geo-map-tooltip-actions">
                    ${pinnedHref
                      ? html`<a class="chart-tooltip-action" href=${pinnedHref} onClick=${(/** @type {MouseEvent} */ event) => handleDrillLinkClick(event, pinnedHref)}>
                          ${seeLeadsText(metric, geoSetCount(hover.counts, metric))}</a>`
                      : null}
                    ${hover.canDrill && level !== 'NEIGHBORHOODS' && hover.pinned.code
                      ? html`<button type="button" class="widget-message-link" onClick=${() => drill(/** @type {string} */ (hover.pinned?.code))}>${t('dashboardDrilldown.geo.drillIn')}</button>`
                      : null}
                  </span>`
                : level === 'NEIGHBORHOODS' && drillController
                  ? html`<span class="geo-map-tooltip-hint">${t('dashboardDrilldown.tooltipHint')}</span>`
                  : hover.canDrill && level !== 'NEIGHBORHOODS' ? html`<span class="geo-map-tooltip-hint">${t('geoMap.tooltip.clickToDrill')}</span>` : null}
            </div>`
          : null}
        ${level === 'NEIGHBORHOODS'
          ? html`<p class="geo-map-hint">${data.points.some((point) => point.cityCode === safePath.cityCode) ? t('geoMap.neighborhoodHint') : t('geoMap.noPoints')}</p>`
          : html`<${GeoLegend} breaks=${breaks} metric=${metric} />`}
      </div>
      <${GeoRanking} areas=${areas} metric=${metric} level=${level} onDrill=${level === 'NEIGHBORHOODS' ? null : drill} />
    </div>
    <${GeoQuality} quality=${data.quality} households=${data.qualityHouseholds} />
  </div>`;
}
